import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Phone, ArrowRight } from 'lucide-react';
import { FadeIn } from './ui/FadeIn';
function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-200 last:border-0" data-id="element-76">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-77">
        <h3 className="text-lg font-semibold text-navy pr-8 group-hover:text-primary transition-colors" data-id="element-78">
          {question}
        </h3>
        <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-gray-50 group-hover:bg-blue-50 transition-colors ${isOpen ? 'bg-blue-50' : ''}`} data-id="element-79">
          <ChevronDown className={`w-5 h-5 text-primary transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-80" />
        </div>
      </button>
      <AnimatePresence data-id="element-81">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden" data-id="element-82">
            <p className="pb-6 text-gray-600 leading-relaxed pr-12" data-id="element-83">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function FAQ() {
  const faqs = [{
    question: 'Do you install custom glass shower doors near me?',
    answer: 'Kosin’s Glass installs custom shower doors and glass shower enclosures throughout Howell, Ann Arbor, Novi and Southeast Michigan. Request a free in-home quote and the team will confirm availability for your location.'
  }, {
    question: 'What is a frameless or Euro glass shower door?',
    answer: 'A frameless or Euro glass shower door uses thick tempered glass and minimal hardware instead of a bulky frame. The result is a clean, open look that helps showcase tile, stone and bathroom finishes.'
  }, {
    question: 'Do you install semi-frameless, framed and sliding shower doors?',
    answer: 'Yes. Kosin’s Glass offers frameless, Euro, semi-frameless, framed, sliding, bypass, tub enclosure, fixed-panel and walk-in shower glass options.'
  }, {
    question: 'Can you replace an existing glass shower door?',
    answer: 'Yes. Kosin’s Glass can review an outdated, damaged, leaking or poorly fitting shower door and recommend replacement or repair options based on the opening and condition of the glass and hardware.'
  }, {
    question: 'How much do custom glass shower doors cost?',
    answer: 'Pricing depends on the shower opening, glass size and thickness, door style, hardware finish, glass texture, protective coating and installation requirements. Kosin’s Glass provides a free in-home quote after reviewing your project.'
  }, {
    question: 'When should shower glass be measured during a bathroom remodel?',
    answer: 'Contact Kosin’s Glass early for planning help. Final measurements are usually taken after key finished surfaces are complete so the glass can be fabricated to the exact opening.'
  }, {
    question: 'What glass and hardware options can I choose?',
    answer: 'Kosin’s Glass offers 18 glass designs and textures plus many hardware finishes, colors and styles. Options may include clear, frosted, rain, reeded, textured or tinted glass depending on availability and project needs.'
  }, {
    question: 'Are frameless shower doors hard to clean?',
    answer: 'Frameless shower doors have fewer frame edges and crevices than traditional framed doors, which can make cleaning easier. Ask about optional protective coatings that help reduce water spots and soap scum.'
  }, {
    question: 'Do glass shower doors leak?',
    answer: 'Professional measurement, proper layout, quality hardware and clean installation help improve water containment. Because every shower is different, Kosin’s Glass can review your layout and explain the best enclosure option.'
  }, {
    question: 'Do you work with bathroom remodelers and contractors?',
    answer: 'Yes. Kosin’s Glass works with homeowners, remodelers, designers, builders and contractors on custom shower glass projects.'
  }];
  return <section className="py-20 bg-white" data-id="element-84">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-85">
        <FadeIn className="text-center mb-12" data-id="element-86">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-6" data-id="element-87">
            Glass Shower Door Questions
          </h2>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-88">
          <div className="bg-white rounded-2xl" data-id="element-89">
            {faqs.map((faq, i) => <FAQItem key={i} question={faq.question} answer={faq.answer} data-id="element-90" />)}
          </div>
        </FadeIn>
      </div>
    </section>;
}
export function FinalCTA() {
  return <section className="py-24 bg-navy text-white relative overflow-hidden" data-id="element-91">
      <div className="absolute inset-0 bg-[url('https://cdn.magicpatterns.com/uploads/8gPVfgFKnKmQ5Gy26EKfHX/GSDpic1.webp')] opacity-15 bg-cover bg-center mix-blend-overlay" data-id="element-92"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-navy to-navy/80" data-id="element-93"></div>

      <div className="container mx-auto px-4 max-w-4xl relative z-10 text-center" data-id="element-94">
        <FadeIn data-id="element-95">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-96">
            Ready to Upgrade Your Bathroom With Custom Shower Glass?
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-97">
            Whether you want a frameless shower door, Euro glass enclosure,
            sliding tub door or custom walk-in shower panel, Kosin’s Glass can
            help you choose the right glass, hardware and installation plan.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-98">
            <a href="#quote-form" className="w-full sm:w-auto bg-primary hover:bg-primary-hover text-white font-semibold py-4 px-8 rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-99">
              Request a Free In-Home Shower Quote
              <ArrowRight className="w-5 h-5" data-id="element-100" />
            </a>
            <a href="tel:5175457772" className="w-full sm:w-auto bg-white/10 hover:bg-white/20 text-white font-semibold py-4 px-8 rounded-xl transition-colors flex items-center justify-center gap-2 backdrop-blur-sm" data-id="element-101">
              <Phone className="w-5 h-5" data-id="element-102" />
              Call 517-545-7772
            </a>
          </div>
        </FadeIn>
      </div>
    </section>;
}
export function StickyMobileCTA() {
  return <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 flex gap-3 z-50 md:hidden shadow-[0_-10px_30px_rgba(15,30,61,0.1)]" data-id="element-103">
      <a href="tel:5175457772" className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-navy font-semibold py-3.5 px-4 rounded-xl active:bg-gray-200 transition-colors" data-id="element-104">
        <Phone className="w-5 h-5" data-id="element-105" />
        Call
      </a>
      <a href="#quote-form" className="flex-[1.5] flex items-center justify-center gap-2 bg-primary text-white font-semibold py-3.5 px-4 rounded-xl active:bg-primary-hover transition-colors" data-id="element-106">
        Request Shower Quote
      </a>
    </div>;
}